// Phase P.36 — Bank CSV import modal.
//
// Flow: parent triggers via `open + previewData`, we render 3 tabs
// (Matched / Unmatched / Discrepancies) with row-level checkboxes,
// and post the user's selections to /finance/import-bank-csv/apply.

import { useEffect, useMemo, useState } from "react";
import { api, formatApiError, formatCurrency } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CheckCircle2, AlertCircle, HelpCircle, Loader2 } from "lucide-react";

export default function BankCsvImportDialog({
  open, onOpenChange, preview, onApplied,
}) {
  // preview shape: { format, matched: [{csv, candidate_id, candidate, score}], unmatched: [{...csv}], discrepancies: [...], summary }
  const [selMatched, setSelMatched] = useState(new Set());
  const [selUnmatched, setSelUnmatched] = useState(new Set());
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Auto-select ALL rows on both matched and unmatched tabs so a
    // single-click "Apply" flow is the default. User can uncheck the
    // ones they don't want.
    setSelMatched(new Set((preview?.matched || []).map((m) => m.candidate_id)));
    setSelUnmatched(new Set((preview?.unmatched || []).map((u) => u.idx)));
  }, [open, preview]);

  const counts = preview?.summary || { matched: 0, unmatched: 0, discrepancies: 0 };

  const toggle = (setter, id) => {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const apply = async () => {
    setApplying(true);
    try {
      const body = {
        reconcile_matches: (preview?.matched || [])
          .filter((m) => selMatched.has(m.candidate_id))
          .map((m) => ({
            candidate_id: m.candidate_id,
            statement_ref: (m.csv?.description || "").slice(0, 200),
          })),
        create_from_unmatched: (preview?.unmatched || [])
          .filter((u) => selUnmatched.has(u.idx))
          .map((u) => ({
            date: u.date,
            description: u.description,
            amount: u.amount,
          })),
      };
      const r = await api.post("/finance/import-bank-csv/apply", body);
      toast.success(`Reconciled ${r.data.reconciled} · Created ${r.data.created}`);
      onApplied?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl" data-testid="bank-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Bank CSV import
            {preview?.format && (
              <span className="mono text-[10px] uppercase tracking-widest bg-muted rounded px-1.5 py-0.5">
                {preview.format}
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            Review matched inflows, one-click create anything unmatched, or hand-fix discrepancies.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue={counts.matched ? "matched" : (counts.unmatched ? "unmatched" : "discrepancies")}>
          <TabsList>
            <TabsTrigger value="matched" data-testid="bank-import-tab-matched">
              <CheckCircle2 className="h-3.5 w-3.5 mr-1 text-emerald-600" /> Matched ({counts.matched})
            </TabsTrigger>
            <TabsTrigger value="unmatched" data-testid="bank-import-tab-unmatched">
              <HelpCircle className="h-3.5 w-3.5 mr-1 text-amber-600" /> Unmatched ({counts.unmatched})
            </TabsTrigger>
            <TabsTrigger value="discrepancies" data-testid="bank-import-tab-discrepancies">
              <AlertCircle className="h-3.5 w-3.5 mr-1 text-rose-600" /> Discrepancies ({counts.discrepancies})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="matched">
            <div className="max-h-[45vh] overflow-y-auto border border-border rounded">
              {(preview?.matched || []).length === 0 && (
                <div className="p-6 text-center text-sm text-muted-foreground">Nothing matched.</div>
              )}
              {(preview?.matched || []).map((m) => (
                <label key={m.candidate_id} className="flex items-start gap-3 px-3 py-2 border-b border-border last:border-b-0 cursor-pointer hover:bg-muted/40" data-testid={`bank-import-matched-row-${m.candidate_id}`}>
                  <input type="checkbox" checked={selMatched.has(m.candidate_id)} onChange={() => toggle(setSelMatched, m.candidate_id)} className="mt-1 accent-emerald-600" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-3">
                      <div className="text-sm font-semibold truncate">{m.csv.description}</div>
                      <div className="mono tabular-nums font-bold text-emerald-700">
                        {formatCurrency(m.csv.amount, "EUR")}
                      </div>
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
                      CSV · {m.csv.date || "—"} → matches <span className="mono">{m.candidate.source_label || m.candidate.customer_name || m.candidate_id.slice(0, 8)}</span>
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="unmatched">
            <div className="max-h-[45vh] overflow-y-auto border border-border rounded">
              {(preview?.unmatched || []).length === 0 && (
                <div className="p-6 text-center text-sm text-muted-foreground">Nothing unmatched — nice.</div>
              )}
              {(preview?.unmatched || []).map((u) => (
                <label key={u.idx} className="flex items-start gap-3 px-3 py-2 border-b border-border last:border-b-0 cursor-pointer hover:bg-muted/40" data-testid={`bank-import-unmatched-row-${u.idx}`}>
                  <input type="checkbox" checked={selUnmatched.has(u.idx)} onChange={() => toggle(setSelUnmatched, u.idx)} className="mt-1 accent-amber-600" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-3">
                      <div className="text-sm font-semibold truncate">{u.description}</div>
                      <div className="mono tabular-nums font-bold">
                        {formatCurrency(u.amount, "EUR")}
                      </div>
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      {u.date || "—"} · Ticking this creates a manual payment marked reconciled.
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="discrepancies">
            <div className="max-h-[45vh] overflow-y-auto border border-border rounded">
              {(preview?.discrepancies || []).length === 0 && (
                <div className="p-6 text-center text-sm text-muted-foreground">No discrepancies.</div>
              )}
              {(preview?.discrepancies || []).map((d) => (
                <div key={d.candidate_id} className="px-3 py-2 border-b border-border last:border-b-0 bg-rose-50" data-testid={`bank-import-discrepancy-row-${d.candidate_id}`}>
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="text-sm font-semibold truncate">{d.csv.description}</div>
                    <div className="mono tabular-nums font-bold text-rose-700">
                      Δ {formatCurrency(d.diff, "EUR")}
                    </div>
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
                    CSV € {formatCurrency(d.csv.amount, "EUR")} vs expected € {formatCurrency(d.candidate.received_amount, "EUR")} · <span className="mono">{d.candidate.source_label || d.candidate.customer_name}</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    Fix by hand on the Finance page — update the Received amount, then re-import.
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="btn-cta" onClick={apply} disabled={applying} data-testid="bank-import-apply">
            {applying ? (<><Loader2 className="h-4 w-4 mr-1 animate-spin" />Applying…</>) : `Apply selected (${selMatched.size + selUnmatched.size})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
